import { useState } from 'react'
import { useAdminTenantUsers, AdminUser } from '@/hooks/useAdminUsers'
import { useAdminTenants } from '@/hooks/useAdminTenants'
import { useImpersonation } from '@/hooks/useImpersonation'
import UserDetailDrawer from './UserDetailDrawer'

export default function TenantUsersTab() {
  const [search, setSearch] = useState('')
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const { impersonate, isStarting } = useImpersonation()
  const { data: tenantsData } = useAdminTenants({ status: 'active', limit: 200 })

  const { data, isLoading, error } = useAdminTenantUsers({
    search: search || undefined,
    tenant_id: selectedTenantId || undefined,
  })

  const handleImpersonate = async (tenantId: string) => {
    try {
      await impersonate(tenantId)
      setSelectedUserId(null)
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start impersonation')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#e8eaed]">Tenant Users</h2>
          <p className="text-sm text-[#8b949e]">Manage B2B customer user permissions</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {/* Tenant Filter */}
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="w-full sm:w-48 px-3 py-2 bg-[#141c24] border border-[#2a3542] rounded-lg text-[#e8eaed] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="">All Tenants</option>
            {tenantsData?.tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>

          {/* Search */}
          <div className="w-full sm:w-64">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 bg-[#141c24] border border-[#2a3542] rounded-lg text-[#e8eaed] placeholder-[#8b949e] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg p-12 text-center text-[#8b949e]">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          Loading users...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <p className="text-red-400">Failed to load users. Please try again.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && data?.users.length === 0 && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg p-12 text-center">
          <p className="text-[#8b949e]">No tenant users found</p>
        </div>
      )}

      {/* User Table */}
      {!isLoading && !error && data && data.users.length > 0 && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-[#2a3542]">
            <thead className="bg-[#0a0f14]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Features
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a3542]">
              {data.users.map((user) => (
                <TenantUserRow
                  key={user.id}
                  user={user}
                  onSelect={() => setSelectedUserId(user.id)}
                  onImpersonate={() => user.tenant_id && handleImpersonate(user.tenant_id)}
                  isImpersonating={isStarting}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Results Info */}
      {data && (
        <p className="text-sm text-[#8b949e]">
          Showing {data.users.length} of {data.total} users
        </p>
      )}

      {/* User Detail Drawer */}
      <UserDetailDrawer
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onImpersonate={handleImpersonate}
      />
    </div>
  )
}

function TenantUserRow({
  user,
  onSelect,
  onImpersonate,
  isImpersonating,
}: {
  user: AdminUser
  onSelect: () => void
  onImpersonate: () => void
  isImpersonating: boolean
}) {
  return (
    <tr className="hover:bg-[#1c2630] transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-semibold">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium text-[#e8eaed]">{user.name}</div>
            <div className="text-sm text-[#8b949e]">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm text-[#e8eaed]">{user.tenant_name || '-'}</div>
          {user.tenant_slug && (
            <div className="text-xs text-[#8b949e] font-mono">{user.tenant_slug}</div>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.is_active
              ? 'bg-green-500/20 text-green-400'
              : 'bg-red-500/20 text-red-400'
          }`}
        >
          {user.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-wrap gap-1">
          {user.feature_overrides.length > 0 ? (
            <>
              {user.feature_overrides.slice(0, 2).map((feature) => (
                <span
                  key={feature}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-teal-500/20 text-teal-400"
                >
                  {feature}
                </span>
              ))}
              {user.feature_overrides.length > 2 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-[#2a3542] text-[#8b949e]">
                  +{user.feature_overrides.length - 2}
                </span>
              )}
            </>
          ) : (
            <span className="text-xs text-[#8b949e]">Default only</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#8b949e]">
        {user.last_login
          ? new Date(user.last_login).toLocaleDateString()
          : 'Never'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex justify-end gap-2">
          {user.tenant_id && (
            <button
              onClick={onImpersonate}
              disabled={isImpersonating}
              className="px-3 py-1.5 text-sm font-medium text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors disabled:opacity-50"
              title="Impersonate this tenant"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </button>
          )}
          <button
            onClick={onSelect}
            className="px-3 py-1.5 text-sm font-medium text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 rounded-lg transition-colors"
          >
            Manage
          </button>
        </div>
      </td>
    </tr>
  )
}
